import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';

const AuthPlaceholder = () => null;

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList, defaultDataSourceName } = appConfig;
  const getDicomStoreNameFromDicomwebRoot = (dicomwebRoot?: string) => {
    const raw = String(dicomwebRoot || '');
    // e.g. .../dicomStores/Stratagen/dicomWeb -> Stratagen
    const match = raw.match(/\/dicomStores\/([^/]+)\/dicomWeb\/?$/i);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  };

  const getParamCaseInsensitive = (sp: URLSearchParams, key: string) => {
    if (sp.has(key)) return sp.get(key);
    const target = key.toLowerCase();
    for (const [k, v] of sp.entries()) {
      if (k.toLowerCase() === target) return v;
    }
    return null;
  };

  const sp = new URLSearchParams(window?.location?.search || '');
  const dicomwebRoot =
    getParamCaseInsensitive(sp, 'dicomwebRoot') || getParamCaseInsensitive(sp, 'dicomWebRoot');
  const dicomStorePath =
    getParamCaseInsensitive(sp, 'dicomStore') ||
    getParamCaseInsensitive(sp, 'dicomStorePath') ||
    getParamCaseInsensitive(sp, 'gcpDicomStorePath');

  const storeNameFromStorePath = (() => {
    if (!dicomStorePath) return null;
    // projects/.../dicomStores/<storeName>
    const match = String(dicomStorePath).match(/\/dicomStores\/([^/]+)\/?$/i);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  })();

  const storeNameFromUrl =
    storeNameFromStorePath || getDicomStoreNameFromDicomwebRoot(dicomwebRoot || '');

  const storeNameFromLocalStorage = (() => {
    try {
      const dsName = defaultDataSourceName || 'dicomweb';
      const storageKey = `ohif.dataSource.${dsName}.config`;
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null;
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const root = parsed?.qidoRoot || parsed?.wadoRoot || parsed?.wadoUriRoot;
      return getDicomStoreNameFromDicomwebRoot(root);
    } catch {
      return null;
    }
  })();

  const storeName = storeNameFromUrl || storeNameFromLocalStorage;

  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>
          {storeName
            ? `You don’t have permission to the DICOM store “${storeName}”.`
            : 'One or more of the requested studies are not available at this time.'}
        </h4>
        {storeName && (
          <p className="mt-2">
            Ask an administrator to grant access.
          </p>
        )}
        {showStudyList && (
          <p className="mt-2">
            Return to the <Link className="text-primary-light" to="/">study list</Link> to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  // Auth-related routes are handled by OpenIdConnectRoutes, but App routes are
  // rendered in parallel. These placeholders prevent briefly falling back to
  // the NotFound (404) route during redirects.
  {
    path: `/login`,
    children: AuthPlaceholder,
  },
  {
    path: `/logout`,
    children: AuthPlaceholder,
  },
  {
    path: `/callback`,
    children: AuthPlaceholder,
  },
  {
    path: `/signin-oidc`,
    children: AuthPlaceholder,
  },
  {
    path: `/silent-refresh.html`,
    children: AuthPlaceholder,
  },
  {
    path: `/logout-redirect.html`,
    children: AuthPlaceholder,
  },
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkList, servicesManager, extensionManager },
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
